import { effectScope, onMounted, onBeforeUnmount, ref } from 'vue';
import { useSpeechSynthesis } from '@vueuse/core';
import type { LanguagesShort } from 'src/types/Languages';
import { detectLanguage } from 'shared/utilities/string';

/** An utterance that hasn't started by then is treated as not spoken. */
export const START_TIMEOUT_MS = 4000;

export function useTts(options?: { lang?: LanguagesShort }) {
  const pitch = ref(1);
  const rate = ref(1);
  const isReady = ref(false);
  const isSpeaking = ref(false);
  const voices = ref<SpeechSynthesisVoice[]>([]);
  const isSupported = ref(false);

  const supportedLanguages: LanguagesShort[] = ['en', 'uk', 'he'];

  // One speech-synthesis instance per useTts, created lazily on the first
  // utterance inside a scope owned by the caller's setup (disposed with it).
  const synthScope = effectScope();
  let synth: ReturnType<typeof useSpeechSynthesis> | undefined;
  // Settles the in-flight utterance's promise; set while one is pending.
  let interruptCurrent: (() => void) | null = null;
  // Set when we cancel an utterance: its events (an 'interrupted'/'canceled'
  // error and/or an end — browsers send one or both) arrive later on the
  // shared utterance object, so until the next utterance's onstart those
  // events are not its own.
  let awaitingStart = false;

  /** Settle the in-flight utterance (as its 'interrupted' error used to). */
  function interruptInFlight() {
    if (!interruptCurrent) return;
    awaitingStart = true;
    interruptCurrent();
  }

  // Check if the Web Speech API is available in this environment
  const checkSupport = (): boolean => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.warn('TTS: Speech synthesis not supported in this environment');
      return false;
    }

    return true;
  };

  function loadVoices(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.speechSynthesis) {
        reject(new Error('Speech synthesis not supported'));
        return;
      }

      const maxAttempts = 6;
      let attempts = 0;
      const intervalId = setInterval(() => {
        if (refreshVoices()) {
          clearInterval(intervalId);
          resolve();
        } else if (++attempts >= maxAttempts) {
          clearInterval(intervalId);
          reject(new Error('No voices found'));
        }
      }, 400);
    });
  }

  /** Reads the browser's voices now; true when a supported voice exists. */
  function refreshVoices(): boolean {
    const available = window.speechSynthesis.getVoices() ?? [];
    voices.value = available.filter((v) =>
      supportedLanguages.some((lang) => v.lang.startsWith(lang)),
    );
    return voices.value.length > 0;
  }

  function getVoiceByText(text?: string) {
    const lang = text ? detectLanguage(text) : (options?.lang ?? 'en-US');
    const match = voices.value.find((v) => v.lang.startsWith(lang));
    return match;
  }

  // Enhanced utter function with better timing control
  async function utter(text: string, options?: { delay?: number }) {
    if (!isSupported.value) {
      console.warn('TTS: Speech synthesis not supported, skipping utterance');
      return Promise.resolve(false);
    }

    if (!text || text.length < 2 || text === ' ') {
      console.warn('TTS: Invalid text, skipping utterance');
      return Promise.resolve(false);
    }

    // Voices are often available before the first 400 ms poll: check now
    // instead of dropping the utterance.
    if (!isReady.value && window.speechSynthesis && refreshVoices()) {
      isReady.value = true;
    }

    if (!isReady.value) {
      console.warn('TTS: Not ready, skipping utterance');
      return Promise.resolve(false);
    }

    console.debug('TTS: Starting utterance:', text);

    // Truncate long text
    if (text.length > 500) {
      text = text.slice(0, 500);
    }

    try {
      // Apply delay if specified
      if (options?.delay && options.delay > 0) {
        await new Promise<void>((resolve) => {
          // Pass function reference to setTimeout to avoid implied eval linter warning
          setTimeout(() => resolve(), options.delay);
        });
      }

      const voice = getVoiceByText(text);
      if (!voice) {
        console.warn('TTS: No voice found for text');
        return Promise.resolve(false);
      }

      if (!synth && synthScope.active) {
        synth = synthScope.run(() =>
          useSpeechSynthesis(text, { pitch, rate, lang: voice.lang }),
        );
      }
      const instance = synth;
      if (!instance) {
        console.warn('TTS: Speech synthesis disposed, skipping utterance');
        return false;
      }

      const u = instance.utterance.value;
      u.text = text;
      u.voice = voice;
      u.lang = voice.lang;
      u.pitch = pitch.value;
      u.rate = rate.value;

      // speak() cancels whatever is in flight.
      interruptInFlight();

      return new Promise<boolean>((resolve, reject) => {
        let started = false;
        let settled = false;
        // Some browsers (iOS Safari without a user gesture) never start the
        // utterance and fire no event at all; don't leave the caller hanging.
        const startWatchdog = setTimeout(() => {
          if (!started) settle(() => resolve(false));
        }, START_TIMEOUT_MS);
        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          clearTimeout(startWatchdog);
          isSpeaking.value = false;
          if (interruptCurrent === interrupt) interruptCurrent = null;
          fn();
        };
        const interrupt = () => settle(() => reject(new Error('interrupted')));
        // A real failure (not a cancel) still settles before the start.
        const isStray = (e?: SpeechSynthesisErrorEvent) =>
          !started &&
          awaitingStart &&
          (!e || e.error === 'interrupted' || e.error === 'canceled');
        interruptCurrent = interrupt;

        u.onstart = () => {
          started = true;
          awaitingStart = false;
          if (!settled) isSpeaking.value = true;
        };
        u.onend = () => {
          if (isStray()) return;
          settle(() => {
            console.debug('TTS: Web Speech API completed');
            resolve(true);
          });
        };
        u.onerror = (e) => {
          if (isStray(e)) return;
          settle(() => {
            console.debug('TTS: Web Speech API error:', e);
            reject(new Error(e.error || 'Speech synthesis error'));
          });
        };

        instance.speak();
      });
    } catch (error) {
      console.error('TTS: Error during utterance:', error);
      isSpeaking.value = false;
      return false;
    }
  }

  function stop() {
    if (!isSupported.value) return;

    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    interruptInFlight();
    isSpeaking.value = false;
  }

  // Suspends the current utterance; `resume()` continues from the same point.
  function pause() {
    if (!isSupported.value || !window.speechSynthesis) return;

    window.speechSynthesis.pause();
    isSpeaking.value = false;
  }

  function resume() {
    if (!isSupported.value || !window.speechSynthesis) return;

    window.speechSynthesis.resume();
    isSpeaking.value = true;
  }

  function reset() {
    pitch.value = 1;
    rate.value = 1;
  }

  onMounted(async () => {
    isSupported.value = checkSupport();

    if (!isSupported.value) {
      console.warn('TTS: Speech synthesis not supported, TTS disabled');
      return;
    }

    try {
      window.speechSynthesis.onvoiceschanged = async () => {
        await loadVoices();
        isReady.value = true;
      };
      await loadVoices();
      isReady.value = true;
    } catch (err) {
      console.error('TTS: Error initializing:', err);
      isSupported.value = false;
    }
  });

  onBeforeUnmount(() => {
    if (isSupported.value) {
      stop();
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    }
  });

  return {
    pitch,
    rate,
    voices,
    isReady,
    isSpeaking,
    isSupported,
    detectLanguage,
    utter,
    pause,
    resume,
    stop,
    reset,
  };
}
